import clsx from 'clsx';
import { useTranslations } from 'i18n';
import * as React from 'react';
import { capitalise, DATE_FNS_CASUAL_DATE_TIME_FORMAT, useDateFormatter, useIntervalFormatter } from 'shared';
import { Text, Tooltip, VisuallyHidden } from 'ui';
import styles from './notification-view.module.css';
import { ReactComponent as OFLogo } from 'assets/of_logo.svg';

interface NotificationViewProps {
    icon?: React.ReactNode;
    title: React.ReactNode;
    contents: React.ReactNode;
    createdAt: Date;
    isRead: boolean;
    onToggleRead?: () => void;
    actions?: React.ReactNode[];
}

export const NotificationView: React.FC<NotificationViewProps> = (props) => {
    const { __ } = useTranslations();
    const formatDate = useDateFormatter();
    const formatInterval = useIntervalFormatter();
    const { icon, title, contents, createdAt, isRead, onToggleRead, actions } = props;
    const readLabel = isRead ? __('notifications.mark_as_unread') : __('notifications.mark_as_read');
    const fullDate = formatDate(createdAt, DATE_FNS_CASUAL_DATE_TIME_FORMAT);

    return (
        <div className={styles['container']}>
            <div className={styles['row']}>
                <div className={styles['icon']}>{icon || <OFLogo />}</div>
                <Text variant="title-small" className="notification-title">
                    {title}
                </Text>
                <div className={styles['right']}>
                    <Tooltip placement="bottom-end" content={fullDate}>
                        <Text as="span" variant="body-small" className="notification-timestamp">
                            <time dateTime={createdAt.toISOString()}>{capitalise(formatInterval(createdAt))}</time>
                            <VisuallyHidden>, {fullDate}</VisuallyHidden>
                        </Text>
                    </Tooltip>
                </div>
            </div>
            <div className={clsx(styles['row'], styles['contents-row'])}>
                <Text as="div" variant="body-small" className="notification-content">
                    {contents}
                </Text>
                {onToggleRead && (
                    <div className={styles['right']}>
                        <Tooltip placement="bottom-end" content={readLabel}>
                            <button
                                type="button"
                                aria-label={readLabel}
                                className={clsx(
                                    styles['read-icon'],
                                    { [styles['is-read']]: isRead },
                                    'notification-read-icon',
                                )}
                                onClick={onToggleRead}
                            />
                        </Tooltip>
                    </div>
                )}
            </div>
            {actions && (
                <div className={styles['actions']}>
                    {actions.map((action, index) => (
                        <div key={index} className="notification-action">
                            {action}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
